import { forwardRef, useEffect, useId, useState } from "react";
import { catalogGenreKey, catalogGenreName, isKnownCatalogGenre } from "@lorevok/domain";
import { loadCatalogFacets, peekCatalogFacets, type CatalogFacets } from "../apiClient.js";
import { track } from "../lib/analytics.js";
import { sameCatalogFilters, type CatalogFilters } from "../lib/catalogFilters.js";
import { useLanguage, useT } from "../lib/i18n/store.js";
import { languageName } from "../lib/languageNames.js";
import { localizeGenre } from "../lib/storyOptions.js";

/** Genre chips shown before "More genres" (the chosen one always shows). */
export const GENRE_CHIPS_SHOWN = 10;

/**
 * The catalog's filter bar: one row of language chips and one of genre chips,
 * single-select each, built from GET /api/catalog/facets (the languages and
 * genres public novels carry). "All …" clears a row; pressing the chosen chip
 * again clears it too. The UI language leads the language row. A genre chip
 * stands for a genre KEY (the domain's catalogGenreKey — one known genre in
 * every language it is tagged in); a known genre is labelled in the UI
 * language, free text as the novels spell it. A chosen value the facets do not
 * list (a stale or hand-typed link) still shows, so it can be cleared — a
 * language by its name, a genre only as "Selected genre": text from the address
 * is never echoed into the page. The language row hides when only one language
 * exists and none is chosen.
 *
 * While the facets are unknown the bar holds its space with a placeholder, so
 * the grid below does not jump when they arrive (a tab that already holds them
 * shows them on the first paint). `enabled` = the signed-out catalog may be
 * read (App's gate: the site mode is known and open); the facets load once it
 * is, and again when `reloadKey` changes (the API came back). The ref is the
 * bar's group element, a target for moving focus after a reset.
 */
export const CatalogFilterBar = forwardRef<
  HTMLDivElement,
  {
    filters: CatalogFilters;
    onChange: (next: CatalogFilters) => void;
    enabled?: boolean;
    reloadKey?: number;
  }
>(function CatalogFilterBar({ filters, onChange, enabled = true, reloadKey = 0 }, ref) {
  const t = useT();
  const uiLanguage = useLanguage();
  const idBase = useId();
  // undefined = not known yet, null = the API could not say.
  const [facets, setFacets] = useState<CatalogFacets | null | undefined>(() => peekCatalogFacets());
  const [allGenres, setAllGenres] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void loadCatalogFacets().then((loaded) => {
      // A failed read keeps what is already shown.
      if (live) setFacets((current) => loaded ?? current ?? null);
    });
    return () => {
      live = false;
    };
  }, [enabled, reloadKey]);

  if (facets === undefined) {
    return (
      <div className="catalog-filters catalog-filters--loading" aria-hidden="true" data-testid="catalog-filters-skeleton">
        <div className="catalog-filter-skeleton skeleton" />
        <div className="catalog-filter-skeleton skeleton" />
      </div>
    );
  }

  const languages = (facets?.languages ?? []).map((entry) => entry.code);
  // The UI language first; the rest keep the facets' order (most novels first).
  languages.sort((a, b) => Number(b === uiLanguage) - Number(a === uiLanguage));
  if (filters.language && !languages.includes(filters.language)) languages.push(filters.language);

  const genreLabel = (key: string, name: string) =>
    isKnownCatalogGenre(key) ? localizeGenre(catalogGenreName(key), t) : name;
  const genres = (facets?.genres ?? []).map((entry) => {
    const key = catalogGenreKey(entry.name);
    return { key, label: genreLabel(key, entry.name) };
  });
  if (filters.genre && !genres.some((genre) => genre.key === filters.genre)) {
    genres.push({ key: filters.genre, label: genreLabel(filters.genre, t("catalog.filterSelectedGenre")) });
  }
  const shownGenres = allGenres
    ? genres
    : genres.filter((genre, index) => index < GENRE_CHIPS_SHOWN || genre.key === filters.genre);

  const showLanguages = languages.length > 1 || filters.language !== null;
  if (!showLanguages && genres.length === 0) return null;

  function choose(kind: "language" | "genre", value: string | null) {
    const next: CatalogFilters = { ...filters, [kind]: value };
    if (sameCatalogFilters(next, filters)) return;
    // Short codes only: a known genre's id ("horror"); one an author or the
    // model typed freely is "other", never its text.
    track("catalog_filter_changed", {
      filter: kind,
      value: value === null ? null : kind === "language" || isKnownCatalogGenre(value) ? value : "other"
    });
    onChange(next);
  }

  const chip = (active: boolean) => (active ? "create-chip catalog-filter-chip active" : "create-chip catalog-filter-chip");

  return (
    <div ref={ref} tabIndex={-1} className="catalog-filters" role="group" aria-label={t("catalog.filtersAria")}>
      {showLanguages && (
        <div className="catalog-filter-row" role="group" aria-labelledby={`${idBase}-language`}>
          <span className="catalog-filter-label" id={`${idBase}-language`}>
            {t("catalog.filterLanguage")}
          </span>
          <div className="create-chips catalog-filter-chips">
            <button
              type="button"
              className={chip(filters.language === null)}
              aria-pressed={filters.language === null}
              onClick={() => choose("language", null)}
            >
              {t("catalog.filterAllLanguages")}
            </button>
            {languages.map((code) => (
              <button
                key={code}
                type="button"
                className={chip(filters.language === code)}
                aria-pressed={filters.language === code}
                onClick={() => choose("language", filters.language === code ? null : code)}
              >
                {languageName(code, uiLanguage)}
              </button>
            ))}
          </div>
        </div>
      )}
      {genres.length > 0 && (
        <div className="catalog-filter-row" role="group" aria-labelledby={`${idBase}-genre`}>
          <span className="catalog-filter-label" id={`${idBase}-genre`}>
            {t("catalog.filterGenre")}
          </span>
          <div className="create-chips catalog-filter-chips">
            <button
              type="button"
              className={chip(filters.genre === null)}
              aria-pressed={filters.genre === null}
              onClick={() => choose("genre", null)}
            >
              {t("catalog.filterAllGenres")}
            </button>
            {shownGenres.map((genre) => (
              <button
                key={genre.key}
                type="button"
                className={chip(filters.genre === genre.key)}
                aria-pressed={filters.genre === genre.key}
                onClick={() => choose("genre", filters.genre === genre.key ? null : genre.key)}
              >
                {genre.label}
              </button>
            ))}
            {genres.length > GENRE_CHIPS_SHOWN && (
              <button
                type="button"
                className="catalog-filter-more"
                aria-expanded={allGenres}
                onClick={() => setAllGenres((open) => !open)}
              >
                {allGenres ? t("catalog.filterFewerGenres") : t("catalog.filterMoreGenres")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
